import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatClassRange, formatGrowthPct, KILL_GROWTH_CLASS_META } from '@/lib/killGrowth';
import { estimateHint } from '@/lib/strengthEstimate';

/**
 * Player-hub Stats card (Plan 051): own monthly M-Sum growth against the
 * power class median. No other names.
 */
export default function StrengthGrowthCard({ token }: { token: string }) {
  const [month, setMonth] = useState<string | undefined>(undefined);
  const q = useQuery({
    queryKey: ['player-strength-growth', token, month ?? 'default'],
    queryFn: () => api.getSelfReportStrengthGrowth(token, month),
    enabled: !!token,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const data = q.data;
  const own = data?.own ?? null;
  const meta = own ? KILL_GROWTH_CLASS_META[own.class] : null;
  const vsMedian =
    own && data?.classMedian?.medianPct != null ? own.pct - data.classMedian.medianPct : null;

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Strength growth
        </h2>
        {data && data.months.length > 1 && (
          <select
            value={data.month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded border border-slate-800 bg-slate-950 px-1.5 py-0.5 text-[11px] text-slate-300"
          >
            {data.months.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        {q.isLoading ? (
          <div className="text-xs text-slate-500">Loading…</div>
        ) : !own || !meta || !data ? (
          <p className="text-xs text-slate-400">
            Not measurable for {data?.monthLabel ?? 'this month'}. Growth is compared
            over whole months — you need a report within 14 days of the month's start
            and end. Updating your values every one or two weeks is enough.
          </p>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div title={estimateHint(own.fromEstimatedFrom, own.toEstimatedFrom)}>
                <div className="text-3xl font-semibold tabular-nums" style={{ color: meta.color }}>
                  {formatGrowthPct(own.pct)}
                </div>
                <div className="text-xs tabular-nums text-slate-400">
                  +{own.deltaM.toFixed(2)} M · {own.fromEstimatedFrom ? '≈' : ''}
                  {own.fromMSum.toFixed(1)} → {own.toEstimatedFrom ? '≈' : ''}
                  {own.toMSum.toFixed(1)} M-Sum
                </div>
                <div className="text-[10px] text-slate-500">{data.monthLabel}</div>
              </div>
              <div className="text-right">
                <span
                  className={cn(
                    'rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                    meta.chip,
                  )}
                >
                  {meta.label}
                </span>
                {data.classMedian &&
                  formatClassRange(data.classMedian.minMSum, data.classMedian.maxMSum) && (
                    <div className="mt-1 text-[10px] tabular-nums text-slate-400">
                      M-Sum {formatClassRange(data.classMedian.minMSum, data.classMedian.maxMSum)}
                    </div>
                  )}
                {own.streak > 1 && (
                  <div className="mt-1 text-[11px] text-amber-300">
                    🔥 {own.streak} months above median
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded border border-slate-800 bg-slate-950/50 px-2.5 py-1.5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">In your class</div>
                <div className="tabular-nums text-slate-100">
                  <span className="text-base font-semibold">#{own.classPosition}</span>{' '}
                  <span className="text-slate-500">of {own.classSize}</span>
                </div>
                {own.aheadOfPct != null && (
                  <div className="text-[10px] text-slate-400">
                    ahead of {Math.round(own.aheadOfPct)} %
                  </div>
                )}
              </div>
              <div className="rounded border border-slate-800 bg-slate-950/50 px-2.5 py-1.5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Class median</div>
                <div className="text-base font-semibold tabular-nums text-slate-100">
                  {formatGrowthPct(data.classMedian?.medianPct ?? null)}
                </div>
                {vsMedian != null && (
                  <div
                    className={cn(
                      'text-[10px] tabular-nums',
                      vsMedian >= 0 ? 'text-emerald-300' : 'text-amber-300',
                    )}
                  >
                    you: {vsMedian >= 0 ? '+' : ''}
                    {vsMedian.toFixed(1)} pts
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
